/**
 * Klarna Payment Info Widget
 * Updates installment price when variant changes
 */

class ProductKlarnaInfo extends HTMLElement {
  constructor() {
    super();
    this.installmentElement = this.querySelector('[data-klarna-installment]');
    this.priceTextElement = this.querySelector('.product-klarna-info__price');
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupObservers());
    } else {
      this.setupObservers();
    }
  }

  setupObservers() {
    // Find the price element in the product detail section
    const priceContainer = document.querySelector('.product-detail__price, .product-single__price');
    
    if (priceContainer) {
      // Watch for price changes
      const observer = new MutationObserver((mutations) => {
        this.checkPriceUpdate();
      });
      
      observer.observe(priceContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
      
      // Initial price update
      this.checkPriceUpdate();
    }

    // Also listen for variant input changes
    const variantInputs = document.querySelectorAll('input[name="id"], .product-variant-id');
    variantInputs.forEach(input => {
      const form = input.closest('form');
      if (form) {
        form.addEventListener('change', () => {
          setTimeout(() => this.checkPriceUpdate(), 100);
        });
      }
    });
  }

  checkPriceUpdate() {
    // Try multiple selectors to find the current price
    const priceSelectors = [
      '.product-detail__price .price-regular .price',
      '.product-detail__price .price',
      '.card-product-price .price-regular .price',
      '.card-product-price .price',
      '.product-single__price .price-regular .price',
      '.product-single__price .price'
    ];
    
    let priceElement = null;
    for (const selector of priceSelectors) {
      priceElement = document.querySelector(selector);
      if (priceElement && priceElement.textContent.trim()) {
        break;
      }
    }
    
    if (priceElement) {
      const priceText = priceElement.textContent.trim();
      const numericPrice = this.extractPrice(priceText);
      if (numericPrice && numericPrice > 0) {
        const installment = Math.round(numericPrice / 3);
        this.updateInstallmentDisplay(installment);
      }
    }
  }

  extractPrice(priceText) {
    // Remove currency symbols and text, keep only numbers and decimal point
    const cleaned = priceText.replace(/[^0-9.,]/g, '');
    if (!cleaned) return 0;
    
    // Handle different decimal separators
    // If there are multiple commas or periods, assume the last one is decimal
    const parts = cleaned.match(/[\d,]+\.?\d*$/);
    if (!parts) return 0;
    
    const normalized = parts[0].replace(/,/g, '');
    return parseFloat(normalized) * 100; // Convert to cents
  }

  updatePrice(variant) {
    if (!variant || !variant.price) return;

    const price = variant.price;
    const installmentPrice = Math.round(price / 3);
    
    this.updateInstallmentDisplay(installmentPrice);
  }

  updateInstallmentDisplay(installmentPrice) {
    if (!this.installmentElement || !this.priceTextElement) return;

    // Update data attribute
    this.installmentElement.dataset.klarnaInstallment = installmentPrice;

    // Format the price using Shopify's money format
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      const moneyFormat = themeGlobalVariables?.settings?.money_format || '{{amount}}';
      const formattedPrice = Shopify.formatMoney(installmentPrice, moneyFormat);
      
      // Get the translation template
      const translationKey = 'products.product.klarna.installments';
      let translationText = this.priceTextElement.textContent;
      
      // Replace the price portion while keeping the text structure
      const pricePattern = /£[\d,]+\.?\d*/g; // Match currency amounts
      if (pricePattern.test(translationText)) {
        translationText = translationText.replace(pricePattern, formattedPrice);
      } else {
        // Fallback: just update with formatted price
        translationText = `or ${formattedPrice} in 3 Monthly Installments`;
      }
      
      this.priceTextElement.textContent = translationText;
    }
  }
}

customElements.define('product-klarna-info', ProductKlarnaInfo);
